import { buildEffectiveDaxGenerationRequest, buildEffectivePageFilters, buildLegacySelectedFilters } from '../../runtime'
import { isSecuredAgentBuilderPlatform } from '../../sections'
import { buildSlicerOptionsRequestFilters, type DrilldownWidgetInput, type HydrateWidgetInput } from '../../widgetData'
import type { DrillOrder } from '../../drilldownHierarchy'
import type {
  DashboardRuntimeState,
  DashboardWidget,
} from '../../types'
import type { DashboardGlobalFilterState, DashboardViewState, WidgetRequestIntent, WidgetSessionState } from './state'

function normalizeWidgetGroupByState(
  widget: DashboardWidget | undefined,
  session: WidgetSessionState,
) {
  const requestedDimensions = session.query.groupByDimensions?.length
    ? session.query.groupByDimensions
    : [
        session.query.groupByDimension
          ?? session.data.groupByDimension
          ?? widget?.rowDimension
          ?? widget?.daxGenerationRequest?.dimensions?.[0]
          ?? null,
      ].filter((value): value is string => Boolean(value))

  const groupByDimensions = widget?.type === 'line'
    ? requestedDimensions.slice(0, 1)
    : requestedDimensions
  const finalGroupByDimensions = groupByDimensions.length > 0 ? groupByDimensions : null

  return {
    groupByDimension: finalGroupByDimensions?.[0] ?? null,
    groupByDimensions: finalGroupByDimensions,
  }
}

function buildRuntimeState(
  appliedFilters: DashboardGlobalFilterState,
  widgets: DashboardViewState['widgets'],
  dashboard: DashboardViewState['dashboard'],
): DashboardRuntimeState {
  const dashboardWidgetsById = new Map((dashboard?.widgets ?? []).map((widget) => [widget.id, widget]))

  return {
    slicerValues: { ...appliedFilters.slicerValues },
    dateRange: { ...appliedFilters.dateRange },
    widgetStateById: Object.fromEntries(
      Object.entries(widgets).map(([widgetId, session]) => [
        widgetId,
        {
          ...normalizeWidgetGroupByState(dashboardWidgetsById.get(widgetId), session),
          measureParameterSelections: session.query.measureParameterSelections ?? undefined,
        },
      ]),
    ),
  }
}

export function buildWidgetHydrateInput(
  state: DashboardViewState,
  widgetId: string,
  intent: WidgetRequestIntent,
  clientId: string,
): HydrateWidgetInput | null {
  const dashboard = state.dashboard
  if (!dashboard) return null

  const widget = dashboard.widgets.find((item) => item.id === widgetId)
  const session = state.widgets[widgetId]
  if (!widget || !session) return null

  const runtimeState = buildRuntimeState(intent.appliedFilters, state.widgets, dashboard)
  const requestGroupByState = normalizeWidgetGroupByState(widget, {
    ...session,
    query: intent.query,
  })

  return {
    clientId: dashboard.clientId ?? clientId,
    dashboardId: state.dashboardId,
    platformTypeId: dashboard.platformTypeId,
    widget,
    effectiveDaxGenerationRequest: buildEffectiveDaxGenerationRequest(dashboard, widget, runtimeState),
    dashboardFilters: dashboard.dashboardFilters ?? [],
    selectedFilters: buildLegacySelectedFilters(dashboard, runtimeState),
    page: intent.query.page,
    pageSize: intent.query.pageSize,
    sortColumn: intent.query.sortColumn,
    sortDirection: intent.query.sortDirection,
    groupByDimension: requestGroupByState.groupByDimension,
    groupByDimensions: requestGroupByState.groupByDimensions,
    fallbackState: {
      totalRows: session.data.totalRows,
      page: session.data.page,
      pageSize: session.data.pageSize,
      sortColumn: session.data.sortColumn,
      sortDirection: session.data.sortDirection,
      groupByDimension: requestGroupByState.groupByDimension,
      groupByDimensions: requestGroupByState.groupByDimensions,
    },
  }
}

export function buildWidgetDrilldownInput(
  state: DashboardViewState,
  widgetId: string,
  intent: WidgetRequestIntent,
  clientId: string,
  drillOrder?: Pick<DrillOrder, 'mode' | 'dimensions'>,
): DrilldownWidgetInput | null {
  const base = buildWidgetHydrateInput(state, widgetId, intent, clientId)
  const drilldown = intent.query.drilldown
  if (!base || !drilldown) return null

  return {
    ...base,
    field: drilldown.field,
    value: drilldown.value,
    dimension: drilldown.dimension,
    ...(drillOrder?.mode === 'override' && drillOrder.dimensions.length > 0
      ? { drillDownDimensions: [...drillOrder.dimensions] }
      : {}),
  }
}

export function buildSlicerOptionsInput(
  state: DashboardViewState,
  field: string,
  clientId: string,
) {
  const dashboard = state.dashboard
  if (!dashboard || (isSecuredAgentBuilderPlatform(dashboard.platformTypeId) && !state.dashboardId)) return null

  const runtimeState = buildRuntimeState(state.appliedFilters, state.widgets, dashboard)

  return {
    clientId: dashboard.clientId ?? clientId,
    dashboardId: state.dashboardId,
    platformTypeId: dashboard.platformTypeId,
    field,
    filters: buildSlicerOptionsRequestFilters(field, buildEffectivePageFilters(dashboard, runtimeState)),
  }
}

export function buildSlicerOptionsRequestKey(input: NonNullable<ReturnType<typeof buildSlicerOptionsInput>>) {
  return JSON.stringify([input.clientId, input.dashboardId, input.platformTypeId, input.field, input.filters])
}

export function shouldUseDrilldownRoute(intent: WidgetRequestIntent, session: WidgetSessionState) {
  return intent.query.drilldown !== null || session.query.drilldown !== null
}
